import { useQuery, UseQueryResult } from 'react-query';
import { assert } from 'ts-essentials';

import { useFeature } from '@src/contexts/features-context/use-feature';
import { useAnalytics } from '@src/features/analytics/hooks';
import { useAuth } from '@src/features/auth/contexts/auth-context';
import { authService } from '@src/features/auth/services';
import { ProfileProviderProps } from '@src/features/profile/contexts/profile-context/types';
import { useMandatoryInfoRedirect } from '@src/features/profile/hooks/use-mandatory-info-redirect';
import { cacheProfile } from '@src/features/profile/lib/profile-cache';
import { profileService } from '@src/features/profile/services';
import { ProfileWithMetadata } from '@src/types/api/models';

import { ProfileContext } from './profile.context';

type ProfileQuery = UseQueryResult<ProfileWithMetadata | null>;

const useProfileGuestIdentification = ({ data: profile, isLoading }: ProfileQuery) => {
  const { trackIdentification } = useAnalytics();

  useQuery(
    ['guest-identification', profile, isLoading],
    async () => {
      assert(profile);

      if (!profile.guestId) {
        // Unexpected, TODO: log/track
        return;
      }

      await trackIdentification({
        profileId: profile.id,
        firstName: profile.firstName,
        lastName: profile.lastName,
        guestId: profile.guestId,
        email: profile.email,
      });
    },
    {
      enabled: !isLoading && !!profile?.guestId,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );
};

export const ProfileProvider = ({ profileCache, children }: ProfileProviderProps) => {
  const guestAccountFeature = useFeature('guestAccount');
  const { isReady } = useAuth();

  const profileQuery = useQuery(
    ['profile', isReady, guestAccountFeature.active],
    async () => profileService.getProfile(),
    {
      initialData: profileCache,
      enabled: isReady && guestAccountFeature.active,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true,
      onSuccess: async (profile) => {
        cacheProfile(profile);
      },
    },
  );

  const logout = async (continueUrl?: string) => {
    await authService.logout({ continueUrl });
  };
  useMandatoryInfoRedirect(profileQuery.data);
  useProfileGuestIdentification(profileQuery);

  return (
    <ProfileContext.Provider
      value={{
        ...profileQuery,
        logout,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};
